import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MaterialModule } from '@fullyops/shared/material.module';
import { normalizeForSearch } from '@fullyops/shared/normalize-for-search';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  debounceTime,
  filter,
  map,
  take,
} from 'rxjs';
import { MatInput } from '@angular/material/input';
import { MatAutocomplete } from '@angular/material/autocomplete';

export interface Named {
  name: string;
}

const maxResultsShown = 100;
@Component({
  selector: 'fo-autocompleting-select',
  templateUrl: './template.html',
  imports: [MaterialModule, CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: AutocompletingSelectComponent,
    },
  ],
})
export class AutocompletingSelectComponent<T extends Named>
  implements OnInit, ControlValueAccessor, MatFormFieldControl<any>
{
  @Input() choices: Observable<T[]>;
  @Input() choiceOptionTemplate: TemplateRef<{ $implicit: T }>;
  @Input() valueField?: keyof T;

  @Input() matchPredicate = (v: T, pattern: string) =>
    normalizeForSearch(v.name).indexOf(pattern) !== -1;
  @Input() autoActiveFirstOption: boolean = true;

  @Input() activeOptionLabel = (v: T) => (v == null ? '' : v.name);

  @Output() onNewOptionCreate = new EventEmitter<string>();
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  set literalText(s: string) {
    this.internalInput.value = s;
  }

  get literalText(): string {
    return this.internalInput.value;
  }

  protected choices$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  protected filteredChoices$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>(
    [],
  );
  protected internalControl = new FormControl<T>(null);
  protected searchText$ = new BehaviorSubject<string>('');

  protected sub = new Subscription();

  ngOnInit() {
    let sub = this.choices.subscribe(this.choices$);
    this.sub.add(sub);
    sub = combineLatest([
      this.choices$,
      this.searchText$.pipe(debounceTime(150)),
    ])
      .pipe(
        map(([allChoices, search]) => {
          const pattern = normalizeForSearch(search as string);
          return allChoices
            .filter((c) => this.matchPredicate(c, pattern))
            .slice(0, maxResultsShown);
        }),
      )
      .subscribe(this.filteredChoices$);
    this.sub.add(sub);
    sub = this.internalControl.valueChanges.subscribe((v) => {
      this.searchText$.next('');
      const valueToEmit = this.valueField && v ? v[this.valueField] : v;
      this.onChange(valueToEmit);
      this.onTouched();
    });
    this.sub.add(sub);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  protected onTextInput(ev: Event) {
    const el = ev.currentTarget as HTMLInputElement;
    this.searchText$.next(el.value);
  }

  // implementation of ControlValueAccessor to enable use with reactive forms

  private onChange = (_: T | T[keyof T]) => {};
  protected onTouched = () => {};

  private writeValueSub?: Subscription;

  writeValue(obj: T | T[keyof T] | null): void {
    if (this.valueField != null && obj && typeof obj !== 'object') {
      // The choices may not be loaded yet; wait until they are so that we can find the value we set
      // among them. Otherwise we would have a race condition.
      this.writeValueSub?.unsubscribe();
      this.writeValueSub = this.choices$
        .pipe(
          map((cs) => cs.find((c) => c[this.valueField] === obj)),
          filter((v) => v != null),
          take(1),
        )
        .subscribe((v) =>
          this.internalControl.setValue(v, { emitEvent: false }),
        );
    } else {
      this.internalControl.setValue(obj as T, { emitEvent: false });
    }
  }

  registerOnChange(f: (v: T) => void): void {
    this.onChange = f;
  }

  registerOnTouched(f: () => void): void {
    this.onTouched = f;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.internalControl.disable();
    } else {
      this.internalControl.enable();
    }
  }

  onPressEnter() {
    if (this.matAutocomplete.isOpen) {
      return;
    }

    const value = this.literalText;
    if (!value || value.trim().length === 0) {
      return;
    }

    const choices = this.choices$.value;
    if (choices.some((choice) => choice.name === value)) {
      return;
    }

    this.onNewOptionCreate.emit(value);
  }

  // implementation of MatFormFieldControl to enable use inside mat-form-field

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  @ViewChild(MatInput, { static: true })
  protected internalInput: MatInput;

  // These are not used when using this component with reactive forms, but they need to be
  // present regardless.
  get value() {
    return this.internalInput.value;
  }
  set value(s: string) {
    this.internalInput.value = s;
  }

  get stateChanges() {
    return this.internalInput.stateChanges;
  }

  get id() {
    return this.internalInput.id;
  }

  get placeholder() {
    return this.internalInput.placeholder;
  }

  get focused() {
    return this.internalInput.focused;
  }

  get empty(): boolean {
    return this.internalInput.empty;
  }

  get shouldLabelFloat(): boolean {
    return this.internalInput.shouldLabelFloat;
  }

  get required() {
    return this.ngControl.control.hasValidator(Validators.required);
  }

  get disabled() {
    return this.ngControl.disabled;
  }

  get errorState() {
    return this.ngControl.touched && this.ngControl.errors !== null;
  }

  get controlType() {
    return this.internalInput.controlType;
  }

  get autofilled() {
    return this.internalInput.autofilled;
  }

  get userAriaDescribedBy() {
    return this.internalInput.userAriaDescribedBy;
  }

  setDescribedByIds(ids: string[]) {
    this.internalInput.setDescribedByIds(ids);
  }

  onContainerClick(_: MouseEvent) {
    this.internalInput.onContainerClick();
  }
}
